import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import type { BusinessCardStatusType } from '@/api/admin-companies-types'
import type { OrderListItem } from '@/api/admin-orders-types'
import { useDeleteBusinessCard, useResetBusinessCardPin } from '@/api/api-mutations'
import { preloadGetBusinessCard, preloadListOrders, useGetBusinessCard, useListOrders } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { Prompt, usePrompt } from '@/components/base/prompt'
import { SearchField } from '@/components/base/search-field'
import { SectionRow } from '@/components/base/section-row'
import { StatusCell, type StatusColor } from '@/components/base/status-cell'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { DEFAULT_PAGE_SIZE, Pagination } from '@/components/table/pagination'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Trash } from '@/icons/trash'
import { ConditionalRouteComponent, PaginatedSearchSchema } from '@/utils/route-utilities'

import { useOrderListColumnDefs } from '../orders/-components'

export const Route = createFileRoute('/_authenticated/_general/business-cards/$cardUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit']}>
			<BusinessCardDetail />
		</ConditionalRouteComponent>
	),
	validateSearch: PaginatedSearchSchema,
	loaderDeps: ({ search: { limit, offset, q } }) => ({ limit, offset, q }),
	loader: async ({ context, params: { cardUuid }, deps: { limit, offset, q } }) => {
		preloadListOrders(context, {
			query: { limit, offset, q, customerCardUuid: cardUuid }
		})
		await preloadGetBusinessCard(context, { path: { cardUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.BusinessCards.Detail.Title()
	}
})

function BusinessCardDetail() {
	const { L } = useI18n()
	const { cardUuid } = Route.useParams()
	const { limit = DEFAULT_PAGE_SIZE, offset, q } = Route.useSearch()

	const card = useGetBusinessCard({ cardUuid })

	const orders = useListOrders({ limit, offset, q, customerCardUuid: cardUuid })
	const orderColumnDefs = useOrderListColumnDefs()

	const navigate = useNavigate()
	const prompt = usePrompt()
	const deleteCard = useDeleteBusinessCard(true)

	const resetPin = useResetBusinessCardPin(true)
	const [pinDialogOpen, setPinDialogOpen] = useState(false)
	const [newPin, setNewPin] = useState<string | null>(null)

	const handleDelete = async () => {
		const confirmed = await prompt({
			title: L.BusinessCards.Delete.Title(),
			description: L.BusinessCards.Delete.Description(),
			confirmText: L.General.Delete(),
			cancelText: L.General.Cancel()
		})
		if (!confirmed) return
		try {
			const result = await deleteCard.mutateAsync({ path: { cardUuid } })
			if (result.isSuccess) {
				toast.success(L.BusinessCards.DeleteSuccess())
				navigate({ to: '/business-cards' })
			} else {
				toast.error(L.Error.SomethingWentWrong())
			}
		} catch (e: any) {
			toast.error(e.message)
		}
	}

	const handleResetPin = async () => {
		try {
			const result = await resetPin.mutateAsync({ path: { cardUuid } })
			if (result.isSuccess) {
				setNewPin(result.response.pin)
				setPinDialogOpen(true)
			} else {
				toast.error('Failed to reset PIN')
			}
		} catch (e: any) {
			toast.error(e.message)
		}
	}

	const handleCloseDialog = () => {
		setPinDialogOpen(false)
		setNewPin(null)
	}

	const customerName = (() => {
		const customer = card.data?.customer
		if (!customer) return '-'
		const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ')
		return name || L.Customers.Anonymous()
	})()

	return (
		<>
			<SingleColumnPage hasOutlet>
				{/* General */}
				<Container className='divide-y'>
					<ContainerHeader
						heading={card.data?.name || L.BusinessCards.Detail.Title()}
						rightSide={
							<Link from={Route.fullPath} to='edit'>
								<IconButton tooltipText={L.General.Edit()}>
									<PencilSquare />
								</IconButton>
							</Link>
						}
						actions={[
							{
								actions: [
									{
										icon: <Trash />,
										label: L.General.Delete(),
										onClick: handleDelete
									}
								]
							}
						]}
					/>
					<SectionRow
						title={L.Fields.Status()}
						value={
							card.data?.status ? (
								<StatusCell color={getCardStatusColor(card.data.status)}>
									{L.BusinessCards.Status(card.data.status)}
								</StatusCell>
							) : (
								'-'
							)
						}
					/>
					{card.data?.company ? (
						<Link to='/companies/$companyUuid' params={{ companyUuid: card.data.company.uuid }}>
							<SectionRow title={L.BusinessCards.Detail.Company()} value={card.data.company.name} />
						</Link>
					) : (
						<SectionRow title={L.BusinessCards.Detail.Company()} value='-' />
					)}
					{card.data?.customer ? (
						<Link to='/customers/$customerUuid' params={{ customerUuid: card.data.customer.uuid }}>
							<SectionRow title={L.BusinessCards.Detail.Customer()} value={customerName} />
						</Link>
					) : (
						<SectionRow title={L.BusinessCards.Detail.Customer()} value='-' />
					)}
				</Container>

				{/* Card Details */}
				<Container className='divide-y'>
					<ContainerHeader heading={L.BusinessCards.Detail.CardDetails()} />
					<SectionRow title={L.BusinessCards.Column.CardNumber()} value={card.data?.cardNumber ?? '-'} />
					<SectionRow title={L.BusinessCards.Column.ExternalId()} value={card.data?.externalId ?? '-'} />
					<SectionRow
						title={L.Fields.CreatedAt()}
						value={card.data?.createdAt ? new Date(card.data.createdAt).toLocaleDateString() : '-'}
					/>
					<SectionRow
						title={L.Fields.UpdatedAt()}
						value={card.data?.updatedAt ? new Date(card.data.updatedAt).toLocaleDateString() : '-'}
					/>
				</Container>

				{/* Security */}
				<Container className='divide-y'>
					<ContainerHeader heading={L.BusinessCards.Detail.Security()} />
					<div className='flex flex-col gap-y-3 px-6 py-4'>
						<Text size='small' leading='compact' className='text-ui-fg-subtle'>
							{L.BusinessCards.Detail.ResetPinDescription()}
						</Text>
						<div>
							<Button
								variant='secondary'
								size='small'
								onClick={handleResetPin}
								isLoading={resetPin.isPending}
							>
								{L.BusinessCards.Detail.ResetPin()}
							</Button>
						</div>
					</div>
				</Container>

				<Container className='flex flex-col'>
					<ContainerHeader heading={L.Orders.Title()} rightSide={<SearchField />} />
					<div className='min-w-0 overflow-x-auto'>
						<Table<OrderListItem>
							className='w-full min-w-[600px]'
							data={orders.data?.orders}
							columnUtilities={{
								getCurrencyCode: ({ currencyCode }) => currencyCode
							}}
							columnDefs={orderColumnDefs}
							animateRows
							suppressCellFocus
							domLayout='autoHeight'
							rowToRouterConfig={row => ({
								to: '/orders/$orderUuid',
								params: { orderUuid: row.uuid }
							})}
						/>
					</div>
					<div className='px-3 py-2 sm:px-6 sm:py-4'>
						<Pagination totalCount={orders.data?.count} />
					</div>
				</Container>
			</SingleColumnPage>

			{/* Reset PIN result dialog */}
			<Prompt open={pinDialogOpen} variant='confirmation'>
				<Prompt.Content>
					<Prompt.Header>
						<Prompt.Title>{L.BusinessCards.Detail.ResetPinSuccess()}</Prompt.Title>
						<Prompt.Description>{L.BusinessCards.Detail.PinViewOnce()}</Prompt.Description>
					</Prompt.Header>
					<div className='mx-6 mt-4 rounded-md border border-ui-border-base bg-ui-bg-subtle px-4 py-3 text-center'>
						<Text size='xlarge' weight='plus' leading='compact' className='font-mono tracking-widest'>
							{newPin}
						</Text>
					</div>
					<Prompt.Footer>
						<Prompt.Action onClick={handleCloseDialog}>{L.General.Close()}</Prompt.Action>
					</Prompt.Footer>
				</Prompt.Content>
			</Prompt>
		</>
	)
}

const getCardStatusColor = (status: BusinessCardStatusType): StatusColor => {
	switch (status) {
		case 'ACTIVE':
			return 'green'
		case 'INACTIVE':
			return 'grey'
		case 'BLOCKED':
			return 'orange'
		default:
			return 'grey'
	}
}
